'use client';

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import logger from '@/lib/logger';

/**
 * User preferences sync hook
 *
 * Provides local-first preferences with background Supabase sync.
 * - Reads from localStorage first for fast initial load
 * - Syncs to Supabase in background when user is authenticated
 * - Falls back to localStorage-only when offline or unauthenticated
 *
 * #734/#727 follow-up: every useUserPreference instance used to run its OWN
 * supabase.auth.getSession() + onAuthStateChange listener + per-key
 * `user_preferences` SELECT. With N instances on a page (2 of them on the live
 * call page) that was N sessions + N listeners + N single-row queries. This now
 * uses a module-level shared store: ONE app-lifetime auth watcher driving an
 * observable session-ready flag (useSyncExternalStore), and ONE deduped
 * all-keys fetch per user feeding an in-memory cache that every instance reads.
 * The public [value, setValue, state] API is unchanged.
 */

type PreferenceValue = string | number | boolean | object | null;

export interface UseUserPreferencesOptions {
  /** Debounce time for syncing to Supabase (ms) */
  syncDebounceMs?: number;
  /** Whether to sync to Supabase (false = localStorage only) */
  enableSync?: boolean;
}

interface PreferenceState<T> {
  value: T;
  isLoading: boolean;
  isSyncing: boolean;
  error: Error | null;
  /** Source of current value: 'local' | 'supabase' | 'default' */
  source: 'local' | 'supabase' | 'default';
}

const CACHE_PREFIX = 'pref:';
const CLOCK_SKEW_TOLERANCE_MS = 5000;

function _persistedWriteTimeKey(key: string): string {
  return `${CACHE_PREFIX}${key}::__wts`;
}

function _readPersistedWriteTime(key: string): number {
  if (typeof window === 'undefined') return 0;
  try {
    const raw = localStorage.getItem(_persistedWriteTimeKey(key));
    const n = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function isPreferencePermissionError(error: unknown): boolean {
  const err = error as { code?: string; status?: number; message?: string } | null;
  const message = err?.message?.toLowerCase() || '';
  return (
    err?.code === '42501' ||
    err?.status === 401 ||
    err?.status === 403 ||
    message.includes('forbidden') ||
    message.includes('permission denied') ||
    message.includes('row-level security')
  );
}

function isTransientError(error: unknown): boolean {
  const msg = (error as { message?: string })?.message?.toLowerCase() || String(error).toLowerCase();
  return ['econnreset', 'etimedout', 'fetch failed', '502', '503', 'socket hang up'].some(p => msg.includes(p));
}

// ──────────────────────────────────────────────────────────────────────────
// Shared module-level store (client-only — this file is 'use client').
// All mutation happens inside effects / async callbacks / event handlers,
// never during render, so it is SSR-safe.
// ──────────────────────────────────────────────────────────────────────────

// — Shared Supabase session-ready (one getSession + one onAuthStateChange) —
let _sessionReady = false;
let _sessionWatcherStarted = false;
const _sessionSubscribers = new Set<() => void>();

function _notifySession() {
  _sessionSubscribers.forEach((cb) => cb());
}

function _startSessionWatcher() {
  if (_sessionWatcherStarted) return;
  _sessionWatcherStarted = true;
  let supabase;
  try {
    supabase = createClient();
  } catch (e) {
    // Allow a later subscriber to retry if client creation failed.
    _sessionWatcherStarted = false;
    logger.warn('useUserPreferences', 'Failed to create Supabase client for session watcher', { error: e });
    return;
  }
  // Initial session (deduped — runs exactly once for the whole app).
  supabase.auth
    .getSession()
    .then(({ data: { session } }) => {
      const ready = !!session;
      if (ready !== _sessionReady) {
        _sessionReady = ready;
        _notifySession();
      }
    })
    .catch((e) => {
      logger.warn('useUserPreferences', 'Failed initial getSession', { error: e });
    });
  // Single app-lifetime listener (never released — it's a singleton, so no
  // StrictMode subscribe/unsubscribe churn).
  supabase.auth.onAuthStateChange((event, session) => {
    // Only SIGNED_OUT drops the cache. TOKEN_REFRESHED fires frequently and must
    // be a cache no-op.
    if (event === 'SIGNED_OUT') {
      _clearPrefsMemoryCache();
    } else if (session) {
      // A fresh/refreshed token clears any prior sync-block so a transient 401
      // (e.g. during a token-refresh gap) can't permanently wedge sync.
      _syncBlocked.clear();
    }
    const ready = !!session;
    if (ready !== _sessionReady) {
      _sessionReady = ready;
      _notifySession();
    }
  });
}

/** React-observable shared session-ready flag (module state needs an external
 * store so a hook that mounts before the session resolves re-renders when it
 * becomes ready). */
function useSharedSessionReady(): boolean {
  return useSyncExternalStore(
    (cb) => {
      _sessionSubscribers.add(cb);
      _startSessionWatcher();
      return () => {
        _sessionSubscribers.delete(cb);
      };
    },
    () => _sessionReady,
    () => false, // server snapshot — never "ready" during SSR
  );
}

// — Shared per-user all-keys preferences cache —
const _prefsCache = new Map<string, Map<string, PreferenceValue>>(); // userId -> key -> value
const _prefsInFlight = new Map<string, Promise<void>>(); // userId -> in-flight all-keys fetch
// Per-key monotonic write-version. setValue bumps a global counter and records
// it per key; an all-keys fetch captures the counter at its START and, on
// resolve, skips any key whose last local write happened AFTER the fetch started
// — so a slow fetch never clobbers a newer local write. Unlike a persistent
// boolean "dirty" flag, this never wedges a key from future server updates after
// a transient sync failure (a later fetch simply has a higher epoch).
let _writeCounter = 0;
const _keyLastWrite = new Map<string, number>(); // "userId::key" -> _writeCounter at last local write
const _keyLastWriteTime = new Map<string, number>(); // keyId -> Date.now() at last local write
const _keySubscribers = new Map<string, Set<() => void>>(); // "userId::key" -> change subscribers
const _syncBlocked = new Set<string>(); // userIds whose RLS/permission blocks sync

function _keyId(userId: string, key: string): string {
  return `${userId}::${key}`;
}

function _notifyKey(userId: string, key: string) {
  _keySubscribers.get(_keyId(userId, key))?.forEach((cb) => cb());
}

function _clearPrefsMemoryCache() {
  _prefsCache.clear();
  _prefsInFlight.clear();
  _keyLastWrite.clear();
  _keyLastWriteTime.clear();
  _syncBlocked.clear();
  // Notify every key subscriber so mounted hooks re-resolve (fall back to
  // localStorage/default for the now-signed-out user).
  _keySubscribers.forEach((subs) => subs.forEach((cb) => cb()));
}

function _recordLocalWrite(userId: string, key: string): void {
  _writeCounter += 1;
  const ts = Date.now();
  _keyLastWrite.set(_keyId(userId, key), _writeCounter);
  _keyLastWriteTime.set(_keyId(userId, key), ts);
  // #727-followup/F2: persist the write time so the dirty-protection survives a page
  // reload (the in-memory _keyLastWriteTime is lost on refresh). Keyed under CACHE_PREFIX
  // so clearPreferencesCache() removes it on logout.
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(_persistedWriteTimeKey(key), String(ts));
    } catch {
      /* localStorage unavailable */
    }
  }
}

function _setCachedValue(userId: string, key: string, value: PreferenceValue) {
  let m = _prefsCache.get(userId);
  if (!m) {
    m = new Map();
    _prefsCache.set(userId, m);
  }
  m.set(key, value);
}

function _getCachedValue(userId: string, key: string): { hit: boolean; value: PreferenceValue } {
  const m = _prefsCache.get(userId);
  if (m && m.has(key)) return { hit: true, value: m.get(key) as PreferenceValue };
  return { hit: false, value: null };
}

/** Fetch ALL of a user's preferences once; concurrent callers share one query. */
function _fetchAllPrefs(userId: string): Promise<void> {
  const existing = _prefsInFlight.get(userId);
  if (existing) return existing;
  const fetchEpoch = _writeCounter; // local writes after this point must win over this fetch
  const p = (async () => {
    try {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('user_preferences')
        .select('key, value, updated_at')
        .eq('user_id', userId);
      if (error) {
        if (error.code === '42P01') {
          logger.debug('useUserPreferences', 'user_preferences table not found, using localStorage');
          return;
        }
        if (isPreferencePermissionError(error)) {
          _syncBlocked.add(userId);
          logger.debug('useUserPreferences', 'No permission to read user_preferences, using localStorage only');
          return;
        }
        throw error;
      }
      const m = _prefsCache.get(userId) ?? new Map<string, PreferenceValue>();
      for (const row of (data ?? []) as Array<{ key: string; value: PreferenceValue; updated_at: string | null }>) {
        const keyId = _keyId(userId, row.key);
        // A local write that landed AFTER this fetch started is newer than the server row.
        if ((_keyLastWrite.get(keyId) ?? 0) > fetchEpoch) continue;
        // #727: don't clobber a local write the server hasn't caught up to yet (e.g. a
        // transient upsert failure left the server row stale). The server's own updated_at
        // tells us if it has caught up — only apply when it's at least as new as our last
        // local write. A genuinely newer value (another device, or our later successful
        // sync) has updated_at >= our write and still applies — no permanent wedge. Legacy
        // rows with null updated_at fall back to the epoch guard above.
        // #727-followup/F2: fall back to the persisted write time + apply skew tolerance.
        const localWriteTime = _keyLastWriteTime.get(keyId) ?? _readPersistedWriteTime(row.key);
        const serverTime = row.updated_at ? Date.parse(row.updated_at) : 0;
        if (localWriteTime > 0 && serverTime > 0 && serverTime < localWriteTime - CLOCK_SKEW_TOLERANCE_MS) continue;
        m.set(row.key, row.value);
        _notifyKey(userId, row.key);
      }
      _prefsCache.set(userId, m);
    } catch (e) {
      if (isPreferencePermissionError(e)) {
        _syncBlocked.add(userId);
        return;
      }
      if (isTransientError(e)) {
        logger.debug('useUserPreferences', 'Transient error fetching preferences, using local values', { error: e });
        return;
      }
      logger.warn('useUserPreferences', 'Failed to fetch user preferences', { error: e });
    } finally {
      _prefsInFlight.delete(userId);
    }
  })();
  _prefsInFlight.set(userId, p);
  return p;
}

/**
 * Hook to manage a single user preference with Supabase sync.
 */
export function useUserPreference<T extends PreferenceValue>(
  key: string,
  defaultValue: T,
  options: UseUserPreferencesOptions = {}
): [T, (value: T) => void, PreferenceState<T>] {
  const { syncDebounceMs = 1000, enableSync = true } = options;
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const isSessionReady = useSharedSessionReady();

  const [state, setState] = useState<PreferenceState<T>>({
    value: defaultValue,
    isLoading: true,
    isSyncing: false,
    error: null,
    source: 'default',
  });

  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);
  // Mirror of the current displayed value, for the cross-instance value-equality
  // guard (avoid no-op setState storms) without adding `state` to effect deps.
  const valueRef = useRef<T>(defaultValue);

  // Safe localStorage access
  const getLocalValue = useCallback((): T | null => {
    if (typeof window === 'undefined') return null;
    try {
      const stored = localStorage.getItem(`${CACHE_PREFIX}${key}`);
      if (stored) {
        return JSON.parse(stored) as T;
      }
    } catch (e) {
      logger.warn('useUserPreferences', `Failed to parse localStorage for ${key}`, { error: e });
    }
    return null;
  }, [key]);

  const setLocalValue = useCallback((value: T) => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(`${CACHE_PREFIX}${key}`, JSON.stringify(value));
    } catch (e) {
      logger.warn('useUserPreferences', `Failed to save to localStorage for ${key}`, { error: e });
    }
  }, [key]);

  // Load initial value from localStorage (fast, local-first).
  useEffect(() => {
    const localValue = getLocalValue();
    if (localValue !== null) {
      valueRef.current = localValue;
      setState(prev => ({ ...prev, value: localValue, source: 'local', isLoading: false }));
    } else {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, [getLocalValue]);

  // Apply the shared-cache value for this key (deduped all-keys fetch).
  // CRITICAL: must check isSessionReady (shared, observable) to avoid 401s from
  // the race where React has the user but the Supabase client hasn't loaded its
  // session from cookies yet.
  useEffect(() => {
    if (!enableSync || !userId || !isSessionReady) return;
    let cancelled = false;

    const applyFromCache = () => {
      if (cancelled || !mountedRef.current) return;
      const { hit, value } = _getCachedValue(userId, key);
      if (!hit) return;
      const v = value as T;
      valueRef.current = v;
      setLocalValue(v);
      setState(prev => ({ ...prev, value: v, source: 'supabase', error: null }));
    };

    // The cache may already be warm (another instance/page mounted earlier).
    applyFromCache();
    // Trigger / await the deduped all-keys fetch, then re-apply.
    _fetchAllPrefs(userId).then(applyFromCache);

    return () => {
      cancelled = true;
    };
  }, [enableSync, userId, key, isSessionReady, setLocalValue]);

  // Subscribe to cross-instance writes of this key so all mounted instances of
  // the same (user, key) stay consistent. Notification only calls setState
  // (never re-triggers sync) and is value-equality-guarded.
  useEffect(() => {
    if (!userId) return;
    const id = _keyId(userId, key);
    const cb = () => {
      if (!mountedRef.current) return;
      const { hit, value } = _getCachedValue(userId, key);
      if (!hit) return;
      const v = value as T;
      try {
        if (JSON.stringify(v) === JSON.stringify(valueRef.current)) return; // no-op guard
      } catch {
        /* non-serializable — fall through and update */
      }
      valueRef.current = v;
      setLocalValue(v);
      setState(prev => ({ ...prev, value: v, source: 'supabase' }));
    };
    let subs = _keySubscribers.get(id);
    if (!subs) {
      subs = new Set();
      _keySubscribers.set(id, subs);
    }
    subs.add(cb);
    return () => {
      subs!.delete(cb);
      if (subs!.size === 0) _keySubscribers.delete(id);
    };
  }, [userId, key, setLocalValue]);

  // Sync to Supabase (debounced, per-key upsert).
  const syncToSupabase = useCallback(async (value: T) => {
    if (!enableSync || !userId || !isSessionReady || _syncBlocked.has(userId)) return;

    if (mountedRef.current) {
      setState(prev => ({ ...prev, isSyncing: true }));
    }

    try {
      const supabase = createClient();
      const { error } = await supabase
        .from('user_preferences')
        .upsert({
          user_id: userId,
          key,
          value,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id,key',
        });

      if (error) {
        if (error.code === '42P01') {
          logger.debug('useUserPreferences', 'user_preferences table not found, skipping sync');
          return;
        }
        if (isPreferencePermissionError(error)) {
          _syncBlocked.add(userId);
          logger.debug('useUserPreferences', `No permission to sync ${key}; using localStorage only`);
          return;
        }
        throw error;
      }

      logger.debug('useUserPreferences', `Synced ${key} to Supabase`, { value });
    } catch (e) {
      if (isPreferencePermissionError(e)) {
        _syncBlocked.add(userId);
        return;
      }
      if (isTransientError(e)) {
        logger.debug('useUserPreferences', `Transient error syncing ${key}, will retry next change`, { error: e });
        return;
      }
      logger.warn('useUserPreferences', `Failed to sync ${key} to Supabase`, { error: e });
      if (mountedRef.current) {
        setState(prev => ({ ...prev, error: e instanceof Error ? e : new Error(String(e)) }));
      }
    } finally {
      if (mountedRef.current) {
        setState(prev => ({ ...prev, isSyncing: false }));
      }
    }
  }, [enableSync, userId, key, isSessionReady]);

  // Set value (local-first, then sync).
  const setValue = useCallback((newValue: T) => {
    valueRef.current = newValue;
    setState(prev => ({ ...prev, value: newValue, source: 'local' }));
    setLocalValue(newValue);

    if (userId) {
      // Optimistically update the shared cache + record the local write version
      // so an in-flight all-keys fetch can't clobber this newer write, and notify
      // other mounted instances of this key.
      _setCachedValue(userId, key, newValue);
      _recordLocalWrite(userId, key);
      _notifyKey(userId, key);
    }

    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }
    syncTimeoutRef.current = setTimeout(() => {
      syncToSupabase(newValue);
    }, syncDebounceMs);
  }, [setLocalValue, syncToSupabase, syncDebounceMs, userId, key]);

  // Cleanup
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  return [state.value, setValue, state];
}

/**
 * Hook to manage multiple preferences at once
 * Useful for migrating existing settings objects
 *
 * @template T - Object type for the preference value (any interface works)
 */
export function useUserPreferencesObject<T extends object>(
  key: string,
  defaultValue: T,
  options: UseUserPreferencesOptions = {}
): [T, (value: Partial<T>) => void, PreferenceState<T>] {
  const [value, setFullValue, state] = useUserPreference(key, defaultValue, options);

  const setPartialValue = useCallback((partial: Partial<T>) => {
    setFullValue({ ...value, ...partial });
  }, [value, setFullValue]);

  return [value, setPartialValue, state];
}

/**
 * Migrate a localStorage key to the new preference system
 * Call this once on app load to migrate existing data
 */
export async function migrateLocalStoragePreference(
  oldKey: string,
  newKey: string,
  transform?: (value: unknown) => unknown
): Promise<boolean> {
  if (typeof window === 'undefined') return false;

  try {
    const oldValue = localStorage.getItem(oldKey);
    if (!oldValue) return false;

    const parsed = JSON.parse(oldValue);
    const transformed = transform ? transform(parsed) : parsed;

    // Save to new location
    localStorage.setItem(`${CACHE_PREFIX}${newKey}`, JSON.stringify(transformed));

    // Remove old key
    localStorage.removeItem(oldKey);

    logger.info('useUserPreferences', `Migrated ${oldKey} -> ${newKey}`);
    return true;
  } catch (e) {
    logger.warn('useUserPreferences', `Failed to migrate ${oldKey}`, { error: e });
    return false;
  }
}

/**
 * Clear all cached preferences (for logout) — both localStorage and the shared
 * in-memory cache.
 */
export function clearPreferencesCache(): void {
  _clearPrefsMemoryCache();

  if (typeof window === 'undefined') return;

  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(CACHE_PREFIX)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach(key => localStorage.removeItem(key));
  logger.info('useUserPreferences', `Cleared ${keysToRemove.length} cached preferences`);
}

export default useUserPreference;
